<script setup lang="ts">
import { AppTextField, AppTypography } from '@/core/components';
import genderService from '@/modules/auth/services/GenderService';
import { GooglePlaceAutocomplete } from '@/modules/search-location/components';
import { nextTick, onMounted, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    isOnboarding?: boolean;
    isDraft?: boolean,
    jobTitleError?: string;
    toBeAdvised?: boolean;
    useDefaultAddress?: string;
    useDefaultAddressItems?: any[];
    errors?: {
      // jobTitle?: string | string[];
      description?: string | string[];
      address?: string | string[];
      payRateMin?: string | string[];
      payRateMax?: string | string[];
      toBeAdvised?: string | string[];
      useDefaultAddress?: string | string[];
    };
  }>(),
  {
    disabled: false,
    isOnboarding: false,
    jobTitleError: '',
    isDraft: false,
    toBeAdvised: false,
    errors: () => ({}),
  }
);
// v-models
// const jobTitle = defineModel<string>('jobTitle');
const description = defineModel<string>('description');
const address = defineModel<string>('address');
const useDefaultAddress = defineModel<string>('useDefaultAddress');
const placeId = defineModel<string>('placeId');
const payRateMin = defineModel<string>('payRateMin');
const payRateMax = defineModel<string>('payRateMax');
const distance_from_me = defineModel<string>('distance_from_me');

// Local refs for toBeAdvised 
const toBeAdvised = ref(props.toBeAdvised);

// Emit events
const emit = defineEmits<{
  'update:toBeAdvised': [value: boolean];
}>();

// Watch for changes and emit events
watch(toBeAdvised, (newValue) => {
  emit('update:toBeAdvised', newValue);
});

// Watch for prop changes and update local refs
watch(() => props.toBeAdvised, (newValue) => {
  toBeAdvised.value = newValue;
});
// Load gender list (example usage)
const genderList = ref<{ data: any[] }>({ data: [] });
const miscLoading = ref(false);

onMounted(async () => {
  miscLoading.value = true;
  await nextTick(); // Ensures DOM is ready
  genderList.value = await genderService.get();
  miscLoading.value = false;
});
</script>

<template>
  <v-form>
    <!-- <div class="form-wrapper">
      <AppTypography type="subheadline-1" class="mb-2">Title of job<span class="required-asterisk">*</span>
      </AppTypography>
      <AppTextField v-model="jobTitle" data-testid="jobTitle" placeholder="Enter Job Title"
        :error-messages="props.errors?.jobTitle" />
    </div> -->

    <div class="form-wrapper">
      <AppTypography type="subheadline-1" class="mb-2">Description of job<span class="required-asterisk">*</span>
      </AppTypography>
      <AppTextField v-model="description" data-testid="description" placeholder="Enter job description"
        :error-messages="props.errors?.description" />
    </div>

    <div class="form-wrapper">
      <AppTypography type="subheadline-1" class="mb-2">Use Default Address <span class="required-asterisk">*</span>
      </AppTypography>
      <v-autocomplete v-model="useDefaultAddress" :items="useDefaultAddressItems" label="Use default address"
        :error-messages="props.errors?.useDefaultAddress" data-testid="useDefaultAddress" 
        :disabled="address ? true : false"
        item-title="full_address" item-value="full_address" />
    </div>

    <div class="form-wrapper">
      <AppTypography type="subheadline-1" class="mb-4">OR</AppTypography>
    </div>

    <div class="form-wrapper mb-6">
      <AppTypography type="subheadline-1" class="mb-2">Address <span class="required-asterisk">*</span></AppTypography>
      <GooglePlaceAutocomplete v-model="address" :place-id="placeId" label="Enter address" :disabled="useDefaultAddress ? true : false"
        :error-messages="props.errors?.address" data-testid="address" />
    </div>

    <div class="form-wrapper">
      <AppTypography type="subheadline-1" class="mb-2">Hourly Rate<span v-if="!toBeAdvised"
          class="required-asterisk">*</span></AppTypography>
      <v-row>
        <v-col cols="6">
          <AppTextField v-model="payRateMin" data-testid="payRateMin" placeholder="Minimum"
            :error-messages="props.errors?.payRateMin" type="text" step="any" prefix="$" :disabled="toBeAdvised" />
        </v-col>
        <v-col cols="6">
          <AppTextField v-model="payRateMax" data-testid="payRateMax" placeholder="Maximum"
            :error-messages="props.errors?.payRateMax" type="text" step="any" prefix="$" :disabled="toBeAdvised" />
        </v-col>
      </v-row>
      <v-checkbox v-model="toBeAdvised" label="Hourly Rate to be Advised"/>
    </div>

    <div class="form-wrapper">
      <v-row>
        <v-col cols="6">
          <AppTypography type="subheadline-1" class="mb-2">Travel <span class="required-asterisk">*</span></AppTypography>
          <v-slider
            v-model="distance_from_me"
            min="0"
            step="5"
            max="150"
            thumb-label
            hide-details="auto"
            thumb-color="white"
            track-color="#E5E5E5"
            track-fill-color="#6E16F4"
          />
          <AppTypography type="body-2" class="text-center">{{ distance_from_me || 0 }}km</AppTypography>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<style lang="scss" scoped>
.form-wrapper {
  margin-bottom: 12px;
}

.required-asterisk {
  color: #f44336 !important;
}
</style>
